import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { StaffService, StaffAttendanceService } from '../services/api';
import { getTerminology } from '../utils/terminology';
import { useSettings } from '../context/SettingsContext';
import Swal from 'sweetalert2';

import { exportToExcel } from '../utils/exportHelper';

const Attendance = () => {
  const [viewType, setViewType] = useState('students'); // 'students' or 'staff'
  const [students, setStudents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const userRole = user?.role || user?.data?.role;
  const canExport = userRole === 'admin' || userRole === 'super_admin';
  const hasStaffAttendancePermission = userRole === 'admin' || userRole === 'super_admin' || user?.staff?.permissions?.includes('manage_staff_attendance');
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [courses, setCourses] = useState([]);
  const [filterCourse, setFilterCourse] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterBatches, setFilterBatches] = useState([]);

  const { settings } = useSettings();

  const terminology = useMemo(() => {
    return getTerminology(settings.school_type);
  }, [settings.school_type]);

  const batchTerm = terminology.batch;
  const batchesTerm = terminology.batches;
  const studentTerm = terminology.student;
  const studentsTerm = terminology.students;

  // Stats
  const [stats, setStats] = useState({ present: 0, absent: 0, late: 0, leave: 0 });

  useEffect(() => {
    loadData(date, viewType);
    loadCourses();
  }, [date, viewType]);

  const loadCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      console.error("Error loading courses", error);
    }
  };

  const handleFilterCourseChange = (e) => {
    const courseId = e.target.value;
    setFilterCourse(courseId);
    setFilterBatch('');
    if (courseId) {
      const selected = courses.find(c => c.id === parseInt(courseId));
      setFilterBatches(selected?.batches || []);
    } else {
      setFilterBatches([]);
    }
  };

  const loadData = async (targetDate, currentView) => {
    try {
      setLoading(true);
      if (currentView === 'students') {
        const [studentsRes, attendanceRes] = await Promise.all([
          api.get('/students'),
          api.get(`/attendance?date=${targetDate}`)
        ]);

        const studentList = studentsRes.data;
        setStudents(studentList);

        const records = attendanceRes.data;
        const initialData = {};
        studentList.forEach(s => { initialData[s.id] = 'present'; });
        records.forEach(r => { initialData[r.student_id] = r.status; });

        setAttendanceData(initialData);
        calculateStats(initialData);
      } else {
        const [staffRes, attendanceRes] = await Promise.all([
          StaffService.getAll(),
          StaffAttendanceService.getAll(targetDate)
        ]);

        const parsedStaffList = staffRes.data;
        setStaffList(parsedStaffList);

        const records = attendanceRes.data;
        const initialData = {};
        parsedStaffList.forEach(s => { initialData[s.id] = 'present'; });
        records.forEach(r => { initialData[r.staff_id] = r.status; });

        setAttendanceData(initialData);
        calculateStats(initialData);
      }
    } catch (error) {
      console.error("Error loading data", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (studentId, status) => {
    const previousStatus = attendanceData[studentId];
    const newData = {
      ...attendanceData,
      [studentId]: status
    };
    setAttendanceData(newData);
    calculateStats(newData);

    try {
      if (viewType === 'students') {
        await api.post('/attendance', {
          student_id: studentId,
          date: date,
          status: status
        });
      } else {
        await api.post('/staff-attendance', {
          staff_id: studentId,
          date: date,
          status: status
        });
      }
    } catch (error) {
      console.error("Error marking attendance", error);
      const revertedData = {
        ...attendanceData,
        [studentId]: previousStatus
      };
      setAttendanceData(revertedData);
      calculateStats(revertedData);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save attendance record.',
        confirmButtonColor: '#0d6efd'
      });
    }
  };

  const calculateStats = (data) => {
    const statsCount = { present: 0, absent: 0, late: 0, leave: 0 };
    Object.values(data).forEach(status => {
      if (statsCount[status] !== undefined) statsCount[status]++;
    });
    setStats(statsCount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'success';
      case 'absent': return 'danger';
      case 'late': return 'warning';
      case 'leave': return 'info';
      default: return 'secondary';
    }
  };

  // No need for uniqueCourses/uniqueBatches anymore as we use courses state

  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      const matchesSearch = (
        student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.reg_no && student.reg_no.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      const matchesCourse = !filterCourse || student.course_id === parseInt(filterCourse);
      const matchesBatch = !filterBatch || student.batch_id === parseInt(filterBatch);

      if (!filterCourse) {
        return false; // Force user to select a course first as per request
      }

      return matchesSearch && matchesCourse && matchesBatch;
    });
  }, [students, searchTerm, filterCourse, filterBatch]);

  const handleExport = () => {
    const list = viewType === 'students' ? filteredStudents : filteredStaff;
    if (list.length === 0) {
      Swal.fire('No Data', 'No records to export.', 'info');
      return;
    }

    const exportData = list.map(item => ({
      'Name': `${item.first_name} ${item.last_name}`,
      [viewType === 'students' ? 'Batch' : 'Designation']: viewType === 'students' ? `${item.course?.name} - ${item.batch?.name}` : item.designation,
      'Status': (attendanceData[item.id] || 'N/A').toUpperCase(),
      'Date': date
    }));

    exportToExcel(exportData, `${viewType}_Attendance_${date}`);
  };


  const filteredStaff = useMemo(() => {
    return staffList.filter(staff => {
      const fullName = `${staff.first_name} ${staff.last_name}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase()) || staff.id.toString().includes(searchTerm);
    });
  }, [staffList, searchTerm]);

  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-end">
        <div>
          <h2 className="fw-bold">Attendance System</h2>
          <p className="text-muted">Mark daily {viewType === 'students' ? studentTerm.toLowerCase() : 'staff'} attendance records</p>
        </div>

        <div className="d-flex align-items-center gap-3">
          {canExport && (
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-3 shadow-sm" onClick={handleExport}>
              <i className="bi bi-file-earmark-spreadsheet"></i> Export
            </button>
          )}
          {hasStaffAttendancePermission && (
            <div className="bg-light p-1 rounded-pill d-flex shadow-sm">
              <button
                className={`btn btn-sm rounded-pill px-4 ${viewType === 'students' ? 'btn-white shadow-sm fw-bold text-primary' : 'text-muted'}`}
                onClick={() => { setViewType('students'); setSearchTerm(''); }}
              >
                {studentsTerm}
              </button>
              <button
                className={`btn btn-sm rounded-pill px-4 ${viewType === 'staff' ? 'btn-primary shadow-sm' : 'btn-light text-muted'}`}
                onClick={() => setViewType('staff')}
              >
                Staff Attendance
              </button>
            </div>
          )}

          <div className="card-box p-2 d-flex align-items-center gap-3 shadow-sm border-0 m-0">
            <input
              type="date"
              className="form-control border-0 bg-light rounded-pill px-3 fw-bold"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ width: 'auto' }}
            />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="row g-4 mb-4">
        <div className="col-md-3">
          <div className="card-box p-3 d-flex align-items-center justify-content-between bg-success-subtle border-success border-opacity-25">
            <div>
              <small className=" fw-bold text-success">Present</small>
              <h3 className="mb-0 fw-bold">{stats.present}</h3>
            </div>
            <i className="bi bi-check-circle-fill text-success fs-1 opacity-50"></i>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card-box p-3 d-flex align-items-center justify-content-between bg-danger-subtle border-danger border-opacity-25">
            <div>
              <small className=" fw-bold text-danger">Absent</small>
              <h3 className="mb-0 fw-bold">{stats.absent}</h3>
            </div>
            <i className="bi bi-x-circle-fill text-danger fs-1 opacity-50"></i>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card-box p-3 d-flex align-items-center justify-content-between bg-warning-subtle border-warning border-opacity-25">
            <div>
              <small className=" fw-bold text-warning">Late</small>
              <h3 className="mb-0 fw-bold">{stats.late}</h3>
            </div>
            <i className="bi bi-clock-history text-warning fs-1 opacity-50"></i>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card-box p-3 d-flex align-items-center justify-content-between bg-info-subtle border-info border-opacity-25">
            <div>
              <small className=" fw-bold text-info">On Leave</small>
              <h3 className="mb-0 fw-bold">{stats.leave}</h3>
            </div>
            <i className="bi bi-journal-medical text-info fs-1 opacity-50"></i>
          </div>
        </div>
      </div>

      <div className="card-box shadow-sm border">

        {/* Filters */}
        <div className="d-flex justify-content-between align-items-center mb-3 px-4 pt-3 pb-3 border-bottom flex-wrap-mobile">
          <h5 className="mb-0 fw-bold text-primary">Attendance Sheet</h5>
          <div className="d-flex gap-3 align-items-center flex-wrap-mobile">
            {viewType === 'students' && (
              <>
                <select
                  className="form-select rounded-pill bg-light border-0 shadow-sm px-3"
                  style={{ height: '40px' }}
                  value={filterCourse}
                  onChange={handleFilterCourseChange}
                >
                  <option value="">Select {terminology.course}...</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>

                <select
                  className="form-select rounded-pill bg-light border-0 shadow-sm px-3"
                  style={{ height: '40px' }}
                  value={filterBatch}
                  onChange={(e) => setFilterBatch(e.target.value)}
                  disabled={!filterCourse}
                >
                  <option value="">All {batchesTerm}</option>
                  {filterBatches.map(batch => (
                    <option key={batch.id} value={batch.id}>{batch.name}</option>
                  ))}
                </select>
              </>
            )}

            <div className="position-relative w-100">
              <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
              <input
                type="text"
                className="form-control rounded-pill ps-5 bg-light border-0"
                placeholder={`Search ${viewType === 'students' ? studentTerm.toLowerCase() : 'staff'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ height: '40px' }}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="ps-4">{viewType === 'students' ? studentTerm : 'Staff'} Name</th>
                <th>{viewType === 'students' ? batchTerm : 'Designation'}</th>
                <th className="text-center">Todays Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="3" className="text-center py-5 text-muted">Loading list...</td></tr>
              ) : viewType === 'students' ? (
                !filterCourse ? (
                  <tr><td colSpan="3" className="text-center py-5 text-muted">Please select a {terminology.course} to view {studentsTerm.toLowerCase()}.</td></tr>
                ) : filteredStudents.length > 0 ? (
                  filteredStudents.map((student) => (
                    <tr key={student.id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="avatar-sm bg-light fw-bold rounded-circle d-flex align-items-center justify-content-center text-dark" style={{ width: 40, height: 40 }}>
                            {student.first_name.charAt(0)}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{student.first_name} {student.last_name}</div>
                            <small className="text-muted">ID: {student.reg_no || `#${student.id}`}</small>
                          </div>
                        </div>
                      </td>
                      <td><span className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-normal">{student.course?.name} - {student.batch?.name}</span></td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          {['present', 'absent', 'late', 'leave'].map((status) => (
                            <button
                              key={status}
                              type="button"
                              className={`btn btn-sm px-3 rounded-pill fw-bold transition-all ${attendanceData[student.id] === status
                                ? `btn-${getStatusColor(status)} shadow-sm scale-110`
                                : 'btn-light text-muted border'
                                }`}
                              onClick={() => handleStatusChange(student.id, status)}
                              style={{
                                transition: 'all 0.2s',
                                transform: attendanceData[student.id] === status ? 'scale(1.05)' : 'scale(1)'
                              }}
                            >
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="text-center py-5 text-muted">No {studentsTerm.toLowerCase()} found in this {terminology.course}.</td></tr>
                )
              ) : (
                filteredStaff.length > 0 ? (
                  filteredStaff.map((staff) => (
                    <tr key={staff.id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="avatar-sm bg-light fw-bold rounded-circle d-flex align-items-center justify-content-center text-dark" style={{ width: 40, height: 40 }}>
                            {staff.first_name.charAt(0)}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{staff.first_name} {staff.last_name}</div>
                            <small className="text-muted">ID: #{staff.id}</small>
                          </div>
                        </div>
                      </td>
                      <td><span className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-normal">{staff.designation}</span></td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          {['present', 'absent', 'late', 'leave'].map((status) => (
                            <button
                              key={status}
                              type="button"
                              className={`btn btn-sm px-3 rounded-pill fw-bold transition-all ${attendanceData[staff.id] === status
                                ? `btn-${getStatusColor(status)} shadow-sm scale-110`
                                : 'btn-light text-muted border'
                                }`}
                              onClick={() => handleStatusChange(staff.id, status)}
                              style={{
                                transition: 'all 0.2s',
                                transform: attendanceData[staff.id] === status ? 'scale(1.05)' : 'scale(1)'
                              }}
                            >
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="text-center py-5 text-muted">No staff found.</td></tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Attendance;
